/* eslint-disable react/prop-types */
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Menu, PanelLeftClose, PanelLeftOpen, Star,
} from 'lucide-react';
import NavItemSubMenu from './NavItemSubMenu';
import styles from './Navbar.module.css';
import { useProfile } from '../../../Providers/ProfileProvider';
import { useLinks } from '../../../Providers/LinksProvider';

export default function Navbar({ open, setOpen }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { profile } = useProfile();
  const { isFavorite, toggleFavorite, trackVisit } = useLinks();
  const navBarLinks = pathname.split('/').slice(1);
  const currentWorkspace = profile?.config?.currentWorkspace;
  const isWorkspacePage = !!currentWorkspace && pathname.startsWith(`/${currentWorkspace}/`);
  const currentLabel = navBarLinks.length
    ? decodeURIComponent(navBarLinks[navBarLinks.length - 1])
    : '';
  const favorited = isWorkspacePage && isFavorite(pathname);

  // isWorkspacePage flips from false to true once the profile finishes
  // loading (it's fetched async in MainPage), *after* the initial pathname
  // is already set -- so it must be a dependency too, or the very first
  // page of a hard navigation never gets tracked.
  useEffect(() => {
    if (!isWorkspacePage || !currentLabel) return;
    trackVisit(pathname, currentLabel);
  }, [pathname, isWorkspacePage, currentLabel]);

  return (
    <nav
      className={styles.navbarContainer}
    >
      {/* Desktop : icône PanelLeft */}
      <button
        type="button"
        className={`${styles.navIconBtnColor} ${styles.panelBtn}`}
        style={{
          background: 'none',
          border: 'none',
          borderRadius: 'var(--radius-xl)',
          color: 'var(--color-text-secondary)',
          cursor: 'pointer',
          justifyContent: 'center',
          alignItems: 'center',
          height: '30px',
          width: '30px',
          padding: '0px',
          marginLeft: 'var(--space-xs)',
        }}
        onClick={() => setOpen(!open)}
      >
        {open ? <PanelLeftClose size={20} /> : <PanelLeftOpen size={20} />}
      </button>
      {/* Mobile : burger */}
      <button
        type="button"
        className={`${styles.navIconBtnColor} ${styles.burgerBtn}`}
        style={{
          background: 'none',
          border: 'none',
          borderRadius: 'var(--radius-xl)',
          color: 'var(--color-text-secondary)',
          cursor: 'pointer',
          justifyContent: 'center',
          alignItems: 'center',
          height: '30px',
          width: '30px',
          padding: '0px',
          marginLeft: 'var(--space-xs)',
        }}
        onClick={() => setOpen(!open)}
      >
        <Menu size={20} />
      </button>
      <div className={styles.breadcrumbWrapper}>
        {navBarLinks.map((el, index) => {
          let link = '';
          for (let i = 0; i <= index; i += 1) {
            if (index === 0) {
              link += `/${navBarLinks[i]}`;
              break;
            }
            if (i === 0) {
              link += `/${navBarLinks[i]}/`;
              // eslint-disable-next-line no-continue
              continue;
            }
            if (i !== index) link += `${navBarLinks[i]}/`;
            else link += navBarLinks[i];
          }
          return (
            <ul
              key={`${el}/${(index * 2) / 2}`}
              className={styles.navbarMenu}
            >
              <NavItemSubMenu
                path={link}
                label={decodeURIComponent(el)}
                disabled={index === navBarLinks.length - 1}
              />
              {index !== navBarLinks.length - 1 && <div style={{ color: 'var(--color-text-secondary)', margin: '5px' }}>/</div>}
            </ul>
          );
        })}
      </div>
      {isWorkspacePage && (
        <button
          type="button"
          className={styles.navIconBtnColor}
          aria-label={favorited ? t('nav.removeFavorite') : t('nav.addFavorite')}
          title={favorited ? t('nav.removeFavorite') : t('nav.addFavorite')}
          style={{
            background: 'none',
            border: 'none',
            borderRadius: 'var(--radius-xl)',
            color: favorited ? 'var(--color-accent)' : 'var(--color-text-secondary)',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '30px',
            width: '30px',
            padding: '0px',
            marginRight: 'var(--space-xs)',
          }}
          onClick={() => toggleFavorite(pathname, currentLabel)}
        >
          <Star size={18} fill={favorited ? 'var(--color-accent)' : 'none'} />
        </button>
      )}
    </nav>
  );
}
